<script lang="ts">
  import {
    showUpgradePrompt,
    upgradePromptContext,
    openPricingModal
  } from '$lib/stores/subscription';
  import { UpgradePromptService } from '$lib/services/subscription/UpgradePromptService';
  import { aiFeaturesStore } from '$lib/stores/aiFeatures';
  import { aiServiceActions, aiServiceState } from '$lib/stores/aiService';
  import { grammarStore } from '$lib/stores/grammarStore';

  // Import prompt components
  import LimitReachedPrompt from './prompts/LimitReachedPrompt.svelte';
  import TrialExpiringPrompt from './prompts/TrialExpiringPrompt.svelte';
  import TrialExpiredPrompt from './prompts/TrialExpiredPrompt.svelte';
  import FeatureLockedPrompt from './prompts/FeatureLockedPrompt.svelte';
  import TextTooLongPrompt from './prompts/TextTooLongPrompt.svelte';
  import AIFeatureLimitPrompt from './prompts/AIFeatureLimitPrompt.svelte';

  function close() {
    const reason = $upgradePromptContext.reason;
    UpgradePromptService.handleModalClose(reason);

    // First set the modal to closed
    showUpgradePrompt.set(false);

    // Reset ALL processing states when closing the modal
    // This ensures the grammar check button is re-enabled

    // 1. Reset AI features store
    aiFeaturesStore.setProcessing(false);
    aiFeaturesStore.reset();

    // 2. Cancel and reset AI service state
    aiServiceActions.cancel();
    // Force reset the isAnalyzing state directly
    aiServiceState.update(state => ({
      ...state,
      isAnalyzing: false
    }));

    // 3. Reset grammar store
    grammarStore.reset();

    // 4. Dispatch event to reset grammar check controller state immediately
    window.dispatchEvent(new CustomEvent('reset-grammar-check-state'));

    // 5. If closing due to text length limit, restore view mode to reading mode
    // (Grammar check switches to source mode but never runs, so restore to original)
    if (reason === 'text_too_long') {
      window.dispatchEvent(new CustomEvent('restore-view-mode-after-limit'));
    }

    // 6. Dispatch again after a small delay to ensure all state updates have propagated
    setTimeout(() => {
      aiServiceState.update(state => ({
        ...state,
        isAnalyzing: false
      }));
      // Dispatch the reset event again to ensure button state is updated
      window.dispatchEvent(new CustomEvent('reset-grammar-check-state'));
    }, 100);
  }
  
  function handleUpgrade() {
    showUpgradePrompt.set(false);
    openPricingModal();

    // Reset ALL processing states when upgrading too
    // 1. Reset AI features store
    aiFeaturesStore.setProcessing(false);
    aiFeaturesStore.reset();

    // 2. Cancel and reset AI service state
    aiServiceActions.cancel();
    aiServiceState.update(state => ({
      ...state,
      isAnalyzing: false
    }));

    // 3. Reset grammar store
    grammarStore.reset();

    // 4. Dispatch event to reset grammar check controller state
    window.dispatchEvent(new CustomEvent('reset-grammar-check-state'));
  }
  
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
    }
  }
  
  $: reason = $upgradePromptContext.reason;
  $: details = $upgradePromptContext.details;
  $: title = UpgradePromptService.getModalTitle(reason, details);
  $: isAIModal = UpgradePromptService.isAIModal(reason, details);
  $: secondaryButtonText = UpgradePromptService.getSecondaryButtonText(reason);
</script>

{#if $showUpgradePrompt}
  <div 
    class="prompt-overlay" 
    role="dialog"
    aria-modal="true"
    aria-labelledby="upgrade-prompt-title"
    tabindex="-1"
    on:click={close} 
    on:keydown={handleKeydown}
  >
    <div 
      class="prompt-content {isAIModal ? 'ai-modal' : ''}" 
      role="presentation"
      on:click|stopPropagation
      on:keydown|stopPropagation
    >
      <button 
        class="close-button" 
        aria-label="닫기"
        on:click={close}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      
      <div class="prompt-header">
        <h3 id="upgrade-prompt-title" class="prompt-title">{title}</h3>
      </div>
      
      <div class="prompt-body">
        {#if reason === 'limit_reached'}
          <LimitReachedPrompt {details} />
        {:else if reason === 'ai_feature_limit'}
          <AIFeatureLimitPrompt {details} />
        {:else if reason === 'trial_expiring'}
          <TrialExpiringPrompt />
        {:else if reason === 'trial_expired'}
          <TrialExpiredPrompt />
        {:else if reason === 'feature_locked'}
          <FeatureLockedPrompt {details} />
        {:else if reason === 'text_too_long'}
          <TextTooLongPrompt {details} />
        {/if}
      </div>
      
      <div class="prompt-actions">
        <button class="action-button secondary" on:click={close}>
          {secondaryButtonText}
        </button>
        <button class="action-button primary" on:click={handleUpgrade}>
          프리미엄 업그레이드 →
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .prompt-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    animation: fadeIn 0.15s ease;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .prompt-content {
    background: white;
    border-radius: 12px;
    max-width: 600px;
    width: 90%;
    padding: 20px 24px;
    position: relative;
    animation: slideUp 0.2s ease;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  }
  
  .prompt-content.ai-modal {
    max-width: 720px;
    padding: 24px 28px;
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .close-button {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s;
  }
  
  .close-button:hover {
    background: #f3f4f6;
    color: #111827;
  }
  
  .prompt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-right: 30px; /* Space for close button */
  }
  
  .prompt-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  .prompt-body {
    color: #6b7280;
    font-size: 13px;
    line-height: 1.5;
  }
  
  .prompt-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }
  
  .action-button {
    flex: 1;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    border: none;
  }
  
  .action-button.secondary {
    background: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
  }
  
  .action-button.secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  
  .action-button.primary {
    background: #8b5cf6;
    color: white;
  }
  
  .action-button.primary:hover {
    background: #7c3aed;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.25);
  }

  /* Dark mode styles */
  @media (prefers-color-scheme: dark) {
    .prompt-overlay {
      background: rgba(0, 0, 0, 0.6);
    }
    
    .prompt-content {
      background: #1f2937;
      color: #d1d5db;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
    }
    
    .prompt-content.ai-modal {
      background: #1f2937;
    }
    
    .close-button {
      color: #6b7280;
    }
    
    .close-button:hover {
      background: #374151;
      color: #f3f4f6;
    }
    
    .prompt-title {
      color: #f3f4f6;
    }
    
    .prompt-body {
      color: #9ca3af;
    }
    
    .action-button.primary {
      background: #8b5cf6;
    }
    
    .action-button.primary:hover {
      background: #7c3aed;
    }
    
    .action-button.secondary {
      background: transparent;
      color: #9ca3af;
      border-color: #374151;
    }
    
    .action-button.secondary:hover {
      background: #111827;
      border-color: #4b5563;
    }
  }
</style>